import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Pencil, ShoppingCart, X } from 'lucide-react';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { QtyStepper } from '../ui/QtyStepper.js';
import { useCourses } from '../stores/courses.js';
import { useCatalog } from '../stores/catalog.js';
import { useUi } from '../stores/ui.js';
import { useShop } from '../stores/shop.js';
import { STORE_LABELS } from '../lib/store.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import type { CatalogItem } from '../api/catalog.js';

const MAX_SUGGESTIONS_DESKTOP = 5;
const MAX_SUGGESTIONS_MOBILE = 4;

export function CourseAddModal(): React.ReactElement | null {
  const open = useUi((s) => s.courseAddOpen);
  const close = useUi((s) => s.closeCourseAdd);
  const add = useCourses((s) => s.add);
  const catalogLoadedStore = useCatalog((s) => s.loadedStore);
  const loadCatalog = useCatalog((s) => s.load);
  const search = useCatalog((s) => s.search);
  const store = useShop((s) => s.store);
  const isMobile = useIsMobile();

  const [value, setValue] = useState('');
  const [selected, setSelected] = useState<CatalogItem | null>(null);
  const [qty, setQty] = useState(1);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setSelected(null);
      setQty(1);
      setHighlightIdx(0);
    }
  }, [open]);

  // Séparé de la remise à zéro ci-dessus : `catalogLoadedStore` change quand le catalogue
  // finit de charger, et un seul effet effaçait alors ce qu'on avait tapé entre-temps.
  useEffect(() => {
    // L'autocomplete ne propose que le catalogue du magasin courant : suggérer un item
    // de l'autre enseigne créerait une course que le backend refuserait (ADR 0011 § 4).
    if (open && catalogLoadedStore !== store) loadCatalog();
  }, [open, catalogLoadedStore, store, loadCatalog]);

  const maxSuggestions = isMobile ? MAX_SUGGESTIONS_MOBILE : MAX_SUGGESTIONS_DESKTOP;
  const suggestions = selected ? [] : search(value).slice(0, maxSuggestions);
  const showFreeFallback = !selected && value.trim().length > 0;
  const optionCount = suggestions.length + (showFreeFallback ? 1 : 0);

  if (!open) return null;

  function chooseSuggestion(item: CatalogItem): void {
    setSelected(item);
    setQty(item.defaultQty);
    setValue(item.name);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function clearSelection(): void {
    setSelected(null);
    setQty(1);
    setValue('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function submit(keepOpen: boolean): Promise<void> {
    if (submitting) return;

    if (selected) {
      setSubmitting(true);
      try {
        await add({ catalogItemId: selected.itemId, qty });
        if (keepOpen) {
          setSelected(null);
          setValue('');
          setQty(1);
          inputRef.current?.focus();
        } else {
          close();
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const trimmed = value.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await add({ title: trimmed });
      if (keepOpen) {
        setValue('');
        inputRef.current?.focus();
      } else {
        close();
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (selected) {
      if (e.key === 'Enter') {
        e.preventDefault();
        void submit(isMobile || e.shiftKey || e.metaKey || e.ctrlKey);
      } else if (e.key === 'Backspace' && value === '') {
        e.preventDefault();
        clearSelection();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (optionCount > 0) setHighlightIdx((i) => Math.min(i + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx < suggestions.length) {
        chooseSuggestion(suggestions[highlightIdx]!);
      } else {
        void submit(isMobile || e.shiftKey || e.metaKey || e.ctrlKey);
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Ajouter une course"
      closeLabel="Fermer"
      anchor="top"
      initialFocusRef={inputRef}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Annuler
          </Button>
          <Button
            onClick={() => void submit(isMobile)}
            onMouseDown={(e) => e.preventDefault()}
            disabled={(!selected && !value.trim()) || submitting}
          >
            Ajouter
          </Button>
        </>
      }
    >
      <div className="pile pile-serree">
        {selected ? (
          <div className="rangee">
            {/* Une puce allumée : l'éteindre retire la sélection. */}
            <button
              type="button"
              className="puce"
              aria-pressed="true"
              onClick={clearSelection}
              aria-label={`Sélection : ${selected.name} — retirer`}
            >
              <ShoppingCart size={14} aria-hidden />
              {selected.name}
              <X size={14} aria-hidden />
            </button>
            <QtyStepper value={qty} onChange={setQty} />
            <input
              ref={inputRef}
              value=""
              onChange={() => {}}
              onKeyDown={onKeyDown}
              className="visuellement-cache"
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        ) : (
          <>
            <Input
              ref={inputRef}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setHighlightIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Pain, lait, lessive…"
              aria-label="Course à ajouter"
            />
            {(suggestions.length > 0 || showFreeFallback) && (
              <div role="listbox" aria-label="Suggestions" className="liste">
                {suggestions.map((item, idx) => (
                  <button
                    key={item.itemId}
                    type="button"
                    role="option"
                    aria-selected={highlightIdx === idx}
                    onClick={() => chooseSuggestion(item)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className="ligne ligne-cliquable"
                  >
                    <ShoppingCart size={16} aria-hidden className="shrink-0 text-muted" />
                    <span className="ligne-corps">
                      <span className="ligne-titre">{item.name}</span>
                    </span>
                    {item.defaultQty > 1 && <span className="ligne-repere">×{item.defaultQty}</span>}
                  </button>
                ))}
                {showFreeFallback && (
                  <button
                    type="button"
                    role="option"
                    aria-selected={highlightIdx === suggestions.length}
                    onClick={() => void submit(isMobile)}
                    onMouseEnter={() => setHighlightIdx(suggestions.length)}
                    className="ligne ligne-cliquable"
                  >
                    <Pencil size={16} aria-hidden className="shrink-0 text-muted" />
                    <span className="ligne-corps">
                      <span className="ligne-titre">
                        Créer &laquo;&nbsp;{value.trim()}&nbsp;&raquo; (libre)
                      </span>
                    </span>
                  </button>
                )}
              </div>
            )}
            <p className="champ-aide max-md:hidden">
              <kbd className="touche">Enter</kbd> pour ajouter ·{' '}
              <kbd className="touche">⇧ Enter</kbd> pour en ajouter d'autres
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
